

import { useState, useEffect, useRef } from 'react'
import { apiFetch } from '@/lib/api'
import { useUser, UserRole, UserOwns } from '@/contexts/UserContext'
import { FiTrash2, FiMoreHorizontal, FiCheck, FiEdit } from 'react-icons/fi'
import { LuLoaderCircle } from 'react-icons/lu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu'
import { Input } from '@/components/ui/shadcn/input'
import dayjs from 'dayjs'

// ---- Render mention markup in comment text ----
export function renderMentions(text) {
    if (!text) return <p />
    const regex = /\{\{\{([\s\S]*?)\}\}\}/g
    let result = []
    let lastIndex = 0
    let match
    let key = 0

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            result.push(<span key={key++}>{text.substring(lastIndex, match.index)}</span>)
        }
        let mention = null
        try {
            mention = JSON.parse('{' + match[1] + '}')
        } catch (e) {
            mention = null
        }
        if (mention && mention.name) {
            result.push(<span key={key++} className="bg-blue-400/30 px-1 rounded-sm">{mention.name}</span>)
        } else {
            result.push(<span key={key++}>{match[0]}</span>)
        }
        lastIndex = regex.lastIndex
    }
    if (lastIndex < text.length) {
        result.push(<span key={key++}>{text.substring(lastIndex)}</span>)
    }
    return <p>{result}</p>
}

// ---- Individual comment ----
export default function Comment({ data, canResolve = true, canEdit = false, canDelete = true, onDelete, onReply, onUpdate, highlightCommentId }) {
    const [comment, setComment] = useState(data)
    const [resolving, setResolving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [editing, setEditing] = useState(false)
    const commentRef = useRef(null)
    const { user } = useUser()
    const [isHighlighted, setIsHighlighted] = useState(false)

    useEffect(() => {
        if (highlightCommentId === comment._id && commentRef.current) {
            setIsHighlighted(true)
            commentRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [comment._id, highlightCommentId])

    const handleResolved = async (res) => {
        try {
            setResolving(true)
            const response = await apiFetch(`/api/shows/${comment.show}/comments/${comment._id}`, {
                method: 'PUT',
                body: JSON.stringify({ resolved: res })
            })
            if (!response.ok) throw new Error('Failed to resolve comment')
            const newComment = await response.json()
            setComment(newComment)
            onUpdate?.(newComment)
        } catch (error) {
            console.error('Error resolving comment:', error)
        } finally {
            setResolving(false)
        }
    }

    const handleDeleteComment = async (id) => {
        try {
            setDeleting(true)
            const response = await apiFetch(`/api/shows/${comment.show}/comments/${id}`, { method: 'DELETE' })
            if (!response.ok) throw new Error('Failed to delete comment')
            await onDelete?.(comment)
        } catch (error) {
            console.error('Error deleting comment:', error)
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div ref={commentRef} className={`flex flex-col p-1 select-none group/comment ${deleting ? 'opacity-30 animate-pulse pointer-events-none' : ''} ${isHighlighted ? 'bg-secondary/50' : ''} ${comment.element_type === 'comment' ? 'pl-2' : ''}`} onMouseEnter={() => setIsHighlighted(false)}>
            <div className='flex justify-between px-1'>
                <div className='flex flex-col'>
                    <div className='text-[10px] text-secondary-foreground/60'>{comment.user?.email}</div>
                    <div className='text-[10px] text-secondary-foreground/60'>{dayjs(comment.createdAt).format('MMM DD @ HH:mm')}</div>
                </div>
                <UserRole role='commentator'>
                    <div className='flex items-center gap-1'>
                        <DropdownMenu>
                            <DropdownMenuTrigger className='opacity-0 group-hover/comment:opacity-100'>
                                <div className='flex items-center bg-secondary text-secondary-foreground hover:bg-secondary/80 p-1 text-xs rounded-md'>
                                    <FiMoreHorizontal className='w-3 h-3' />
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                {canEdit && <DropdownMenuItem className='p-1 text-xs'>
                                    <FiEdit className='w-4 h-4' />
                                    Edit
                                </DropdownMenuItem>}
                                <UserOwns id={comment.user?._id}>
                                    {canDelete && <DropdownMenuItem className='p-1 text-xs' onClick={() => handleDeleteComment(comment._id)}>
                                        <FiTrash2 className='w-4 h-4 text-red-500' />
                                        Delete
                                    </DropdownMenuItem>}
                                </UserOwns>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {canResolve && (
                            <div className={`cursor-pointer opacity-70 hover:opacity-100 ${resolving ? 'pointer-events-none' : ''}`} onClick={() => handleResolved(!comment.resolved)}>
                                {resolving ? <LuLoaderCircle className='animate-spin' /> : <FiCheck className={`w-4 h-4 ${comment.resolved ? 'text-green-500' : 'text-secondary-foreground'}`} />}
                            </div>
                        )}
                    </div>
                </UserRole>
            </div>
            {editing ?
                <Input value={comment.text} className='text-secondary-foreground whitespace-pre-wrap hover:bg-secondary/60 p-1 rounded-sm select-none cursor-pointer text-xs' />
                :
                <div className={`text-secondary-foreground whitespace-pre-wrap p-1 rounded-sm select-none text-xs ${user?.role >= 10 ? 'cursor-pointer hover:bg-secondary/60' : ''}`} onClick={() => user?.role >= 10 ? onReply?.(comment) : null}>
                    {renderMentions(comment.text)}
                </div>
            }
        </div>
    )
}
